import Vue from "vue";
import { Module } from "vuex/types";
import { DynamicDocument, DynamicDocuments } from "@/types";

export interface SaveFailure {
  status: number;
  message: string;
  at: number;
}

// "installation" = PATCH /installations/{id}, "document" = the delivery-document PUT/POSTs.
export type SaveKind = "installation" | "document";

export interface InstallationSaveStatus {
  savedAt?: number;
  failures?: Partial<Record<SaveKind, SaveFailure>>;
  offline?: boolean;
}

export interface SaveStatusState {
  installations: Record<number, InstallationSaveStatus>;
}

const stateFactory = (): SaveStatusState => ({
  installations: {}
});

const documentChanged = (doc?: DynamicDocument) =>
  !!doc?.changedAt && (!doc.meta?.updatedAt || doc.changedAt > doc.meta.updatedAt);

const firstFailure = (item?: InstallationSaveStatus): SaveFailure | undefined =>
  Object.values(item?.failures ?? {}).find(failure => !!failure);

export const module: Module<SaveStatusState, any> = {
  namespaced: true,

  state: stateFactory,

  getters: {
    ["failure"](state) {
      return (id: number): SaveFailure | undefined => firstFailure(state.installations[id]);
    },

    ["offline"](state) {
      return (id: number): boolean => !!state.installations[id]?.offline;
    },

    ["savedAt"](state) {
      return (id: number): number => state.installations[id]?.savedAt ?? 0;
    },

    ["hasUnsavedWork"](state, getters, rootState): boolean {
      const failed = Object.values(state.installations).some(item => !!firstFailure(item));

      const moduleChanges = Object.values(rootState.installation?.installations ?? {}).some(
        (item: any) => Object.keys(item.modules ?? {}).length > 0
      );

      const documentChanges = Object.values(rootState.deliveryDocuments?.documents ?? {}).some(
        docs => Object.values(docs as DynamicDocuments).some(documentChanged)
      );

      return failed || moduleChanges || documentChanges;
    }
  },

  mutations: {
    ["saved"](state, { id, kind, at }: { id: number; kind: SaveKind; at: number }) {
      const failures: InstallationSaveStatus["failures"] = { ...state.installations[id]?.failures };
      delete failures[kind];

      Vue.set(state.installations, id, { savedAt: at, failures });
    },

    ["failed"](
      state,
      { id, kind, failure }: { id: number; kind: SaveKind; failure: SaveFailure }
    ) {
      const item = state.installations[id];

      Vue.set(state.installations, id, {
        ...item,
        failures: { ...item?.failures, [kind]: failure },
        offline: false
      });
    },

    ["offline"](state, id: number) {
      Vue.set(state.installations, id, { ...state.installations[id], offline: true });
    },

    ["reset"](state) {
      Object.assign(state, stateFactory());
    }
  },

  actions: {
    // 4xx responses are left to the existing (validation) handling.
    ["failed"](
      { state, commit },
      { id, kind, status, message }: { id: number; kind: SaveKind; status?: number; message: string }
    ) {
      if (!status) {
        commit("offline", id);
        return;
      }

      if (status < 500) return;

      if (!firstFailure(state.installations[id])) {
        this.dispatch(
          "track",
          `save failed for installation: ${id} (${kind}) => ${status} ${message}`
        );
      }

      commit("failed", { id, kind, failure: { status, message, at: Date.now() } });
    },

    ["reset"]: {
      root: true,
      handler({ commit }) {
        commit("reset");
      }
    }
  }
};

export default module;
